import { CircleMarker, Tooltip } from 'react-leaflet';
import type { CircleMarker as LeafletCircleMarker, PathOptions } from 'leaflet';
import { TEXAS_CITIES } from '@/geo/texasCounties';
import type { ScoredGeography } from '@/types/insights';
import { colorForBand } from './scoreColor';
import { useUiStore } from '@/store/uiStore';

interface CityLayerProps {
  geographies: ScoredGeography[];
}

const BASE_RADIUS = 5;
const HOVER_RADIUS = 8;

/**
 * Approximate point-marker representation of major Texas cities. City
 * boundaries are not available in Phase 1 (see geo/texasCounties.ts) so
 * each city rolls up its PARENT county's score rather than having its own
 * -- clearly a simplification, called out in the tooltip copy. Because
 * there's no real city polygon to trace on hover (unlike CountyLayer),
 * hover feedback here grows/highlights the marker instead of a border
 * outline; the label itself is boxless (no floating tooltip rectangle),
 * matching the county layer's hover treatment.
 */
export function CityLayer({ geographies }: CityLayerProps) {
  const selectGeo = useUiStore((s) => s.selectGeo);
  const byGeoId = new Map(geographies.map((g) => [g.record.geoId, g]));

  return (
    <>
      {TEXAS_CITIES.map((city) => {
        const scored = byGeoId.get(city.countyFips);
        const band = scored?.insights.hotColdScore;
        const baseStyle: PathOptions = {
          color: '#0f172a',
          weight: 1,
          fillColor: band ? colorForBand(band) : '#e2e8f0',
          fillOpacity: 0.85,
        };
        return (
          <CircleMarker
            key={city.geoId}
            center={[city.lat, city.lon]}
            radius={BASE_RADIUS}
            pathOptions={baseStyle}
            eventHandlers={{
              click: () => selectGeo(city.countyFips),
              mouseover: (e) => {
                const marker = e.target as LeafletCircleMarker;
                marker.setStyle({ weight: 3 });
                marker.setRadius(HOVER_RADIUS);
                marker.bringToFront();
              },
              mouseout: (e) => {
                const marker = e.target as LeafletCircleMarker;
                marker.setStyle(baseStyle);
                marker.setRadius(BASE_RADIUS);
              },
            }}
          >
            <Tooltip className="map-hover-label" sticky opacity={1}>
              {city.name} (using {scored?.record.geoName ?? 'parent'} County data)
            </Tooltip>
          </CircleMarker>
        );
      })}
    </>
  );
}
